import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import './Mold.scss';
import moment from 'moment';
import { uploadLabSampleReport } from '../../sevices/jobApi';

const Mold = ({ data, violation_id, className = '', isEditMode = false, onDataChange, onSubmit, formRef, buildingData, apartmentNumber, moldType, ticketData,inspectorCertificate, ...props }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || '';
  const violationNumber = violation_id || data?.violation_id;
  const temperatureImage =
    data?.temperature_image
      ? `${baseUrl}${data?.temperature_image}`
      : '';
  const humidityImage =
    data?.humidity_image
      ? `${baseUrl}${data?.humidity_image}`
      : '';
  const apartmentImage =
    data?.unit_image
      ? `${baseUrl}${data?.unit_image}`
      : '';

  // Construct property address from building data
  const getPropertyAddress = () => {
    if (data?.property_address) return data.property_address;
    if (buildingData?.address) {
      const aptPart = apartmentNumber ? `, Apt #${apartmentNumber}` : '';
      const cityStateZip = buildingData.city && buildingData.state && buildingData.zip_code 
        ? `, ${buildingData.city}, ${buildingData.state} ${buildingData.zip_code}`
        : buildingData.city 
        ? `, ${buildingData.city}`
        : '';
      return `${buildingData.address}${aptPart}${cityStateZip}`;
    }
    return '';
  };

  // Get prepared for information from building/client data
  const getPreparedForName = () => {
    if (data?.prepared_for_name) return data.prepared_for_name;
    if (buildingData?.client_name) return buildingData.client_name;
    return 'Lee Odell';
  };

  const getPreparedForCompany = () => {
    if (data?.client_name) return data.client_name;
    return '';
  };

  const getPreparedForAddress = () => {
    if (data?.client_city && data?.client_state) {
      return `${data.client_address} ${data.client_city}, ${data.client_state} ${data.client_zip_code || ''}`.trim();
    }
    return '';
  };

  const defaultValues = {
    prepared_by_name: data?.prepared_by_name || ticketData?.inspector_name || 'Zamo Kucevic',
    prepared_by_company:  ticketData?.company_name || 'Raza Environmental Inc.',
    assessor_license: data?.business_license || 'Business License #01695',
    inspector_license: inspectorCertificate?.certificate_id || '',
    business_license:  ticketData?.company_license ? 'Business License ' + ticketData?.company_license : '',
    report_prepared_on: data?.inspection_date ? moment(data.inspection_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    property_address: getPropertyAddress(),
    prepared_for_name: getPreparedForName(),
    prepared_for_company: getPreparedForCompany(),
    prepared_for_address: getPreparedForAddress(),
    violation_id: violation_id || '',
    inspection_date: data?.inspection_date ? moment(data.inspection_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    outside_temperature: data?.outside_temperature || '',
    humidity: data?.humidity || '',
    estimated_cost: data?.estimated_cost || '',
    final_conclusion: data?.final_conclusion || 'The results of this assessment have confirmed the presence of damaged materials and or mold growth, as the quantity of mold impacted surfaces is estimated to be 5 square feet, the following recommendations (work plan) are included to provide the necessary protocols to remediate the subject property.',
    apartment_number: data?.apartment_number || apartmentNumber || '2R',
    rooms: data?.rooms || [],
    work_plan_date: data?.work_plan_date ? moment(data.work_plan_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
    // Post-type observations fields (new keys with fallback to legacy names)
    all_remediated_areas_cleaned: data?.all_remediated_areas_cleaned ?? data?.post_areas_cleaned ?? false,
    no_visible_mold_observed: data?.no_visible_mold_observed ?? data?.post_no_visible_mold ?? false,
    repairs_completed: data?.repairs_completed ?? data?.post_repairs_complete ?? false,
    ventilation_adequate: data?.ventilation_adequate ?? data?.post_ventilation_adequate ?? false,
    moisture_source_corrected: data?.moisture_source_corrected ?? data?.post_moisture_corrected ?? false,
    clearance_passed: data?.clearance_passed !== undefined ? data.clearance_passed : true,
    clearance_failed: data?.clearance_failed !== undefined ? data.clearance_failed : false,
    clearance_violation_number: data?.clearance_violation_number || '',
    clearance_corrective_actions: data?.clearance_corrective_actions || ''
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'rooms'
  });

  const formData = watch();
  const [uploadingFiles, setUploadingFiles] = useState({});

  const handleFileUpload = async (lab_sample_id, file, roomIndex, sampleIndex) => {
    if (!file || !lab_sample_id) {
      alert('Please select a file and ensure lab sample ID is available');
      return;
    }

    const key = `${roomIndex}-${sampleIndex}`;
    setUploadingFiles(prev => ({ ...prev, [key]: true }));

    try {
      await uploadLabSampleReport({ lab_sample_id, file });
      alert('File uploaded successfully');
      // Optionally refresh data or update UI
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [key]: false }));
    }
  };

  useEffect(() => {
    if (data || buildingData) {
      reset({
        prepared_by_name: data?.prepared_by_name || defaultValues.prepared_by_name,
        prepared_by_company: data?.prepared_by_company || defaultValues.prepared_by_company,
        business_license: data?.business_license || defaultValues.business_license,
        assessor_license: data?.assessor_license || defaultValues.assessor_license,
        inspector_license: inspectorCertificate?.certificate_id || defaultValues.inspector_license,
        report_prepared_on: data?.inspection_date ? moment(data.inspection_date).format('YYYY-MM-DD') : defaultValues.report_prepared_on,
        property_address: getPropertyAddress(),
        prepared_for_name: getPreparedForName(),
        prepared_for_company: getPreparedForCompany(),
        prepared_for_address: getPreparedForAddress(),
        violation_id: violation_id || defaultValues.violation_id,
        inspection_date: data?.inspection_date ? moment(data.inspection_date).format('YYYY-MM-DD') : defaultValues.inspection_date,
        outside_temperature: data?.outside_temperature || defaultValues.outside_temperature,
        humidity: data?.humidity || defaultValues.humidity,
        estimated_cost: data?.estimated_cost || defaultValues.estimated_cost,
        final_conclusion: data?.final_conclusion || defaultValues.final_conclusion,
        apartment_number: data?.apartment_number || apartmentNumber || defaultValues.apartment_number,
        rooms: data?.rooms || [],
        work_plan_date: data?.work_plan_date ? moment(data.work_plan_date).format('YYYY-MM-DD') : defaultValues.work_plan_date,
        all_remediated_areas_cleaned: data?.all_remediated_areas_cleaned ?? data?.post_areas_cleaned ?? defaultValues.all_remediated_areas_cleaned,
        no_visible_mold_observed: data?.no_visible_mold_observed ?? data?.post_no_visible_mold ?? defaultValues.no_visible_mold_observed,
        repairs_completed: data?.repairs_completed ?? data?.post_repairs_complete ?? defaultValues.repairs_completed,
        ventilation_adequate: data?.ventilation_adequate ?? data?.post_ventilation_adequate ?? defaultValues.ventilation_adequate,
        moisture_source_corrected: data?.moisture_source_corrected ?? data?.post_moisture_corrected ?? defaultValues.moisture_source_corrected,
        clearance_passed: data?.clearance_passed !== undefined ? data.clearance_passed : defaultValues.clearance_passed,
        clearance_failed: data?.clearance_failed !== undefined ? data.clearance_failed : defaultValues.clearance_failed,
        clearance_violation_number: data?.clearance_violation_number || defaultValues.clearance_violation_number,
        clearance_corrective_actions: data?.clearance_corrective_actions || defaultValues.clearance_corrective_actions
      });
    }
  }, [data, violation_id, buildingData, apartmentNumber, reset]);

  // Removed automatic onDataChange call to prevent infinite re-renders
  // onDataChange will be called on form submission instead

  const onSubmitForm = (data) => {
    // Prepare data in the API format
    const apiData = {
      outside_temperature: data.outside_temperature ? parseFloat(data.outside_temperature) : null,
      humidity: data.humidity ? parseFloat(data.humidity) : null,
      final_conclusion: data.final_conclusion || '',
      estimated_cost: data.estimated_cost ? parseFloat(data.estimated_cost) : null,
      work_plan_date: data.work_plan_date || null,
      all_remediated_areas_cleaned: data.all_remediated_areas_cleaned ?? null,
      no_visible_mold_observed: data.no_visible_mold_observed ?? null,
      repairs_completed: data.repairs_completed ?? null,
      ventilation_adequate: data.ventilation_adequate ?? null,
      moisture_source_corrected: data.moisture_source_corrected ?? null,
      clearance_passed: data.clearance_passed !== undefined ? data.clearance_passed : null,
      clearance_failed: data.clearance_failed !== undefined ? data.clearance_failed : null,
      clearance_violation_number: data.clearance_violation_number || '',
      clearance_corrective_actions: data.clearance_corrective_actions || '',
      rooms: (data.rooms || []).map(room => ({
        unit_visual_observation_id: room.unit_visual_observation_id || null,
        room_name: room.room_name || room.name || '',
        visible_mold: room.visible_mold || false,
        visible_moisture: room.visible_moisture || false,
        musty_odor: room.musty_odor || false,
        visible_surface_damage: room.visible_surface_damage || false,
        temperature: room.temperature ? parseFloat(room.temperature) : null,
        humidity: room.humidity ? parseFloat(room.humidity) : null,
        square_feet_affected: room.square_feet_affected ? parseFloat(room.square_feet_affected) : null,
        room_area_sqft: room.room_area_sqft ? parseFloat(room.room_area_sqft) : null,
        surface_cleaned_and_treated: room.surface_cleaned_and_treated || false,
        drywall_replaced: room.drywall_replaced || false,
        caulk_repaired: room.caulk_repaired || false,
        cabinet_and_wall_replaced: room.cabinet_and_wall_replaced || false,
        ventilation_created: room.ventilation_created || false,
        lab_samples: (room.lab_samples || []).map(sample => ({
          lab_sample_id: sample.lab_sample_id || sample.id || null,
          sample_location: sample.sample_location || '',
          sample_type: sample.sample_type || ''
        }))
      }))
    };

    console.log('API Data:', apiData);
    
    if (onDataChange) {
      onDataChange(apiData);
    }
    if (onSubmit) {
      onSubmit(apiData);
    }
  };

  const rooms = fields;

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmitForm)} className={`mold-container ${className}`}>
      <div className="mold-content">
        <div className="mold-form-section">
          <div className="mold-form-field">
            <label>Prepared by:</label>
          
              <div className="mold-form-value">
                <div>{formData.prepared_by_name}</div>
                <div>{formData.prepared_by_company}</div>
              </div>
           
          </div>

          <div className="mold-form-field">
            <label>NYS DOL License ID #:</label>
            
              <div className="mold-form-value">
                <div>Assessor License: {inspectorCertificate?.certificate_id}</div>
                <div>{formData.business_license}</div>
              </div>
           
          </div>

          <div className="mold-form-field">
            <label>Report Prepared On:</label>

              <div className="mold-form-value">{moment(formData.report_prepared_on).format('MM/DD/YYYY')}</div>
            
          </div>

          <div className="mold-form-field">
            <label>Property Address:</label>

              <div className="mold-form-value">{formData.property_address}</div>
          </div>

          <div className="mold-form-field">
            <label>Prepared for:</label>
              <div className="mold-form-value">
                <div>{formData.prepared_for_company}</div>
                <div>{formData.prepared_for_address}</div>
              </div>
          </div>

          <div className="mold-form-field">
            <label>HPD Violation:</label>
            <div className="mold-form-value">{formData.violation_id}</div>
          </div>

          <div className="mold-form-field">
            <label>Date of Inspection:</label>
            <div className="mold-form-value">{moment(formData.inspection_date).format('MM/DD/YYYY')}</div>
          </div>
        </div>

        <div className="mold-image-section">
          <div className="mold-image-placeholder">
            <img 
              src={`${process.env.REACT_APP_BASE_URL}${data?.building_image}`} 
              alt="Property Building" 
              className="mold-building-image"
            />
          </div>
        </div>
      </div>

      <div className="mold-sections">
        <section className="mold-section">
          <div className="mold-section-title">BACKGROUND DETAILS</div>
          <div className="mold-section-divider" />
          <div className="mold-highlight-box">
            <div className="mold-background-content">
              {moldType === 'post' ? (
                <>
                  <div className="mold-background-intro">
                    An initial mold assessment was performed, and a Mold Remediation Work Plan was created on{' '}
                    <strong>
                      {isEditMode ? (
                        <input
                          type="date"
                          className="mold-input-inline"
                          {...register('work_plan_date')}
                        />
                      ) : (
                        formData.work_plan_date ? moment(formData.work_plan_date).format('MMMM D, YYYY') : (data?.work_plan_date ? moment(data.work_plan_date).format('MMMM D, YYYY') : 'January 1,' + moment().format('YYYY'))
                      )}
                    </strong>
                    , in response to conditions associated with HPD Violation #{violationNumber || '---'}.
                  </div>

                  <div className="mold-background-section">
                    <div className="mold-background-intro">
                      The remediation work described in that plan has since been completed by the responsible contractor.
                    </div>
                  </div>

                  <div className="mold-background-section">
                    <div className="mold-background-intro">
                      This post-clearance inspection is being performed to <strong>verify that the remediation work was completed in accordance with the approved remediation plan and applicable NYC regulations.</strong>
                    </div>
                  </div>

                  <div className="mold-background-section">
                    <div className="mold-background-intro">
                      This inspection is <strong>visual-only and non-invasive</strong>, intended solely to confirm visible cleanliness, dryness, and correction of previously identified mold conditions.
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="mold-background-intro">
                    This assessment was performed in response to{' '}
                    <strong>HPD Violation #{violationNumber || '---'}</strong> and is
                    limited strictly to a <strong>non-invasive visual inspection</strong> of
                    the areas accessible at the time of the site visit.
                  </div>

                  <div className="mold-background-section">
                    <div className="mold-background-heading">The purpose of this inspection is to:</div>
                    <ul className="mold-background-list">
                      <li>Document current visible mold conditions</li>
                      <li>Develop an appropriate mold remediation plan in accordance with NYC guidelines</li>
                      <li>Provide recommendations for corrective actions required to clear the violation</li>
                    </ul>
                  </div>

                  <div className="mold-background-section">
                    <div className="mold-background-heading">This inspection is visual-only and non-destructive. Accordingly:</div>
                    <ul className="mold-background-list">
                      <li>No wall, ceiling, or floor assemblies were opened or intruded into</li>
                      <li>No assessment was performed on concealed spaces or conditions behind walls, inside cavities, or within mechanical systems</li>
                      <li>No moisture mapping, air sampling, swab sampling, or laboratory testing was conducted unless otherwise stated</li>
                      <li>Observations are based solely on conditions visible and accessible at the time of inspection</li>
                    </ul>
                  </div>

                  <div className="mold-background-section">
                    <div className="mold-background-heading">Because this assessment does not include destructive or invasive investigation:</div>
                    <ul className="mold-background-list">
                      <li>
                        The inspector <strong>cannot confirm the presence, cause, or extent of mold growth or moisture conditions within concealed building components</strong>
                      </li>
                      <li>
                        The inspector <strong>bears no responsibility for conditions hidden behind walls or other inaccessible areas</strong>
                      </li>
                      <li>
                        Additional concealed mold, moisture intrusion, or structural deterioration may exist but <strong>is outside the scope of this inspection</strong>
                      </li>
                    </ul>
                  </div>

                  <div className="mold-background-conclusion">
                    This report reflects only the <strong>observable mold conditions</strong> relevant to HPD Violation #{violationNumber || '---'} and provides a remediation plan based on those accessible findings.
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

       {moldType === 'pre' && <section className="mold-section">
          <div className="mold-section-title">OUTDOOR WEATHER CONDITIONS</div>
          <div className="mold-section-divider" />

          <div className="mold-weather-grid">
            <div className="mold-weather-card">
              <img
                src={temperatureImage}
                alt="Outdoor Temperature"
                className="mold-weather-image"
              />
              <div className="mold-weather-caption">
                Outdoor Temperature:{' '}
                {isEditMode ? (
                  <input
                    type="text"
                    className="mold-input-inline"
                    {...register('outside_temperature')}
                  />
                ) : (
                  <strong>{formData.outside_temperature || '45.7'}°F</strong>
                )}
              </div>
            </div>

            <div className="mold-weather-card">
              <img
                src={humidityImage}
                alt="Outdoor Relative Humidity"
                className="mold-weather-image"
              />
              <div className="mold-weather-caption">
                Outdoor Relative Humidity:{' '}
                {isEditMode ? (
                  <input
                    type="text"
                    className="mold-input-inline"
                    {...register('humidity')}
                  />
                ) : (
                  <strong>{formData.humidity || '47.9'}%</strong>
                )}
              </div>
            </div>
          </div>
        </section>}

        <section className="mold-section">
          <div className="mold-section-title">APARTMENT PHOTO</div>
          <div className="mold-section-divider" />

          <div className="mold-apartment-grid">
            <div className="mold-apartment-card">
              <img
                src={apartmentImage}
                alt="Apartment Photo"
                className="mold-apartment-image"
              />
              <div className="mold-apartment-caption">
                Apartment:{' '}
                {isEditMode ? (
                  <input
                    type="text"
                    className="mold-input-inline"
                    {...register('apartment_number')}
                  />
                ) : (
                  <strong>{formData.apartment_number}</strong>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="mold-section">
          <div className="mold-section-title">OBSERVATIONS</div>
          <div className="mold-section-divider" />

          <div className="mold-bathrooms-list">
            {rooms.map((room, index) => {
              const tempImage = room.temperature_image
                ? `${baseUrl}${room.temperature_image}`
                : '';
              const humImage = room.humidity_image
                ? `${baseUrl}${room.humidity_image}`
                : '';

              return moldType === 'pre' ? (
                <div key={room.id || index} className="mold-bathroom-card">
                  <div className="mold-bathroom-title">{room.room_name || room.name || `Room #${index + 1}`}</div>

                  <div className="mold-bathroom-subsection">
                    <div className="mold-subsection-title">Visual Findings</div>
                    <div className="mold-findings-list">
                      <div className="mold-finding-item">
                        <span>visible moisture was detected at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.visible_moisture`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>

                      <div className="mold-finding-item">
                        <span>visible mold was detected at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.visible_mold`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>

                      <div className="mold-finding-item">
                        <span>Odor was detected at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.musty_odor`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>

                      <div className="mold-finding-item">
                        <span>visible surface damage/discoloration was observed at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.visible_surface_damage`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mold-square-feet">
                    <span>Square Feet Affected:</span>
                    {isEditMode ? (
                      <input
                        type="text"
                        className="mold-input-small"
                        {...register(`rooms.${index}.square_feet_affected`)}
                      />
                    ) : (
                      <strong>{room.square_feet_affected || '0'} sq.ft.</strong>
                    )}
                  </div>
                 

                  <div className="mold-bathroom-images">
                    <div className="mold-bathroom-image-card">
                      <img
                        src={tempImage}
                        alt={`${room.name} Temperature`}
                        className="mold-bathroom-image"
                      />
                      <div className="mold-bathroom-image-caption">
                        {room.name} Temperature:{' '}
                        {isEditMode ? (
                          <input
                            type="text"
                            className="mold-input-inline"
                            {...register(`rooms.${index}.temperature`)}
                          />
                        ) : (
                          <strong>{room.temperature || '0'}°F</strong>
                        )}
                      </div>
                    </div>

                    <div className="mold-bathroom-image-card">
                      <img
                        src={humImage}
                        alt={`${room.name} Relative Humidity`}
                        className="mold-bathroom-image"
                      />
                      <div className="mold-bathroom-image-caption">
                        {room.name} Relative Humidity:{' '}
                        {isEditMode ? (
                          <input
                            type="text"
                            className="mold-input-inline"
                            {...register(`rooms.${index}.humidity`)}
                          />
                        ) : (
                          <strong>{room.humidity || '0'}%</strong>
                        )}
                      </div>
                    </div>
                  </div>

                  {room.mold_images && room.mold_images.length > 0 && (
                    <div className="mold-bathroom-subsection">
                      <div className="mold-subsection-title">Mold Images</div>
                      <div className="mold-bathroom-images">
                        {room.mold_images.map((image, imageIndex) => {
                          const imageUrl = image
                            ? `${baseUrl}${image}`
                            : '';
                          return (
                            <div key={imageIndex} className="mold-bathroom-image-card">
                              <img
                                src={imageUrl}
                                alt={`${room.name} Additional Image ${imageIndex + 1}`}
                                className="mold-bathroom-image"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {room.lab_samples && room.lab_samples.length > 0 && (
                  <div className="mold-bathroom-subsection">
                    <div className="mold-subsection-title">Lab Samples</div>
                    {room.lab_samples.map((sample, sampleIndex) => (
                      <div key={sampleIndex} className="mold-lab-samples">
                        {isEditMode ? (
                          <>
                            <div>
                              Sample Location:{' '}
                              <input
                                type="text"
                                className="mold-input-inline"
                                {...register(`rooms.${index}.lab_samples.${sampleIndex}.sample_location`)}
                              />
                            </div>
                            <div>
                              Type:{' '}
                              <input
                                type="text"
                                className="mold-input-inline"
                                {...register(`rooms.${index}.lab_samples.${sampleIndex}.sample_type`)}
                              />
                            </div>
                            <div>
                              <label className="mold-file-upload-label">
                                Upload Report:
                                <input
                                  type="file"
                                  className="mold-file-input"
                                  disabled={uploadingFiles[`${index}-${sampleIndex}`]}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file && sample.lab_sample_id) {
                                      handleFileUpload(sample.lab_sample_id, file, index, sampleIndex);
                                    } else if (!sample.lab_sample_id) {
                                      alert('Lab sample ID is missing. Cannot upload file.');
                                    }
                                  }}
                                />
                                {uploadingFiles[`${index}-${sampleIndex}`] && (
                                  <span className="mold-upload-status">Uploading...</span>
                                )}
                              </label>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>Sample Location: {sample.sample_location || 'N/A'}</div>
                            <div>Type: {sample.sample_type || 'N/A'}</div>
                            {sample.lab_report_file && (
                              <div>
                                <a href={`${baseUrl}${sample.lab_report_file}`} target="_blank" rel="noopener noreferrer">
                                  View Report
                                </a>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                  <div className="mold-bathroom-subsection">
                    <div className="mold-subsection-title">Remediation</div>
                    <ul className="mold-remediation-list">
                      {isEditMode ? (
                        <>
                          <li>
                            <label className="mold-checkbox-label">
                              <Controller
                                name={`rooms.${index}.ventilation_created`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="checkbox"
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                  />
                                )}
                              />
                              <span className='ml-2'>Create Ventilation source</span>
                            </label>
                          </li>
                          <li>
                            <label className="mold-checkbox-label">
                              <Controller
                                name={`rooms.${index}.surface_cleaned_and_treated`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="checkbox"
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                  />
                                )}
                              />
                              <span className='ml-2'>Clean and scrub affected surfaces -an EPA-registered antimicrobial cleaner, repaint with mold-resistant coating encapsulant or clear sealant to inhibit regrowth.</span>
                            </label>
                          </li>
                          <li>
                            <label className="mold-checkbox-label">
                              <Controller
                                name={`rooms.${index}.drywall_replaced`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="checkbox"
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                  />
                                )}
                              />
                              <span className='ml-2'>Replace drywall - Should be cut back at least 12 inches beyond the last sign of visible mold/damage.</span>
                            </label>
                          </li>
                          <li>
                            <label className="mold-checkbox-label">
                              <Controller
                                name={`rooms.${index}.caulk_repaired`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="checkbox"
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                  />
                                )}
                              />
                              <span className='ml-2'>Remove caulk; clean seam with antimicrobial; dry and apply mildew-resistant silicone</span>
                            </label>
                          </li>
                          <li>
                            <label className="mold-checkbox-label">
                              <Controller
                                name={`rooms.${index}.cabinet_and_wall_replaced`}
                                control={control}
                                render={({ field }) => (
                                  <input
                                    type="checkbox"
                                    checked={field.value || false}
                                    onChange={(e) => field.onChange(e.target.checked)}
                                  />
                                )}
                              />
                              <span className='ml-2'>Replace damaged cabinet base and rear wall; clean surrounding drywall and framing with EPA-registered antimicrobial cleaner, dry area completely.</span>
                            </label>
                          </li>
                        </>
                      ) : (
                        <>
                          {(formData.rooms?.[index]?.ventilation_created || room.ventilation_created) && (
                            <li>Create Ventilation source</li>
                          )}
                          {(formData.rooms?.[index]?.surface_cleaned_and_treated || room.surface_cleaned_and_treated) && (
                            <li>Clean and scrub affected surfaces -an EPA-registered antimicrobial cleaner, repaint with mold-resistant coating encapsulant or clear sealant to inhibit regrowth.</li>
                          )}
                          {(formData.rooms?.[index]?.drywall_replaced || room.drywall_replaced) && (
                            <li>Replace drywall - Should be cut back at least 12 inches beyond the last sign of visible mold/damage.</li>
                          )}
                          {(formData.rooms?.[index]?.caulk_repaired || room.caulk_repaired) && (
                            <li>Remove caulk; clean seam with antimicrobial; dry and apply mildew-resistant silicone</li>
                          )}
                          {(formData.rooms?.[index]?.cabinet_and_wall_replaced || room.cabinet_and_wall_replaced) && (
                            <li>Replace damaged cabinet base and rear wall; clean surrounding drywall and framing with EPA-registered antimicrobial cleaner, dry area completely.</li>
                          )}
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              ) : (
                <div key={room.id || index} className="mold-bathroom-card">
                  <div className="mold-bathroom-title">{room.room_name || room.name || `Room #${index + 1}`}</div>
                  <div className="mold-bathroom-subsection">
                    <div className="mold-subsection-title">Visual Findings</div>
                    <div className="mold-findings-list">
                      <div className="mold-finding-item">
                        <span>visible moisture was detected at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.visible_moisture`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>

                      <div className="mold-finding-item">
                        <span>visible mold was detected at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.visible_mold`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>

                      <div className="mold-finding-item">
                        <span>Odor was detected at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.musty_odor`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>

                      <div className="mold-finding-item">
                        <span>visible surface damage/discoloration was observed at the time of inspection</span>
                        <div className="mold-radio-group">
                          <Controller
                            name={`rooms.${index}.visible_surface_damage`}
                            control={control}
                            render={({ field }) => (
                              <>
                                <label>
                                  <input
                                    type="radio"
                                    checked={field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(true)}
                                  />
                                  Yes
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    checked={!field.value}
                                    disabled={!isEditMode}
                                    onChange={() => field.onChange(false)}
                                  />
                                  No
                                </label>
                              </>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {room.mold_images && room.mold_images.length > 0 && (
                    <div className="mold-bathroom-subsection">
                      <div className="mold-subsection-title">Mold Images</div>
                      <div className="mold-bathroom-images">
                        {room.mold_images.map((image, imageIndex) => {
                          const imageUrl = image
                            ? `${baseUrl}${image}`
                            : '';
                          return (
                            <div key={imageIndex} className="mold-bathroom-image-card">
                              <img
                                src={imageUrl}
                                alt={`${room.name} Additional Image ${imageIndex + 1}`}
                                className="mold-bathroom-image"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {moldType === 'post' && (
          <>
            <section className="mold-section">
              <div className="mold-section-title">OBSERVATIONS</div>
              <div className="mold-section-divider" />
              <div className="mold-highlight-box">
                <div className="mold-background-content">
                  <ul className="mold-background-list" style={{ listStyle: 'disc', paddingLeft: '20px' }}>
                    <li className='post-type-observation-list'>
                      <span>All remediated areas appear properly cleaned (circle one)</span>
                      <div>
                      <Controller
                        name="all_remediated_areas_cleaned"
                        control={control}
                        render={({ field }) => {
                          const value = field.value ?? formData.all_remediated_areas_cleaned ?? false;
                          return (
                            <>
                              <label style={{ marginLeft: '12px', marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === true}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(true)}
                                />
                                Yes
                              </label>
                              <label style={{ marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === false}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(false)}
                                />
                                No
                              </label>
                          
                            </>
                          );
                        }}
                      />
                      </div>
                    </li>
                    <li className='post-type-observation-list'>
                      <span>No visible mold growth was observed in previously affected areas (circle one)</span>
                      <div>
                      <Controller
                        name="no_visible_mold_observed"
                        control={control}
                        render={({ field }) => {
                          const value = field.value ?? formData.no_visible_mold_observed ?? false;
                          return (
                            <>
                              <label style={{ marginLeft: '12px', marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === true}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(true)}
                                />
                                Yes
                              </label>
                              <label style={{ marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === false}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(false)}
                                />
                                No
                              </label>
                             
                            </>
                          );
                        }}
                      />
                      </div>
                    </li>
                    <li className='post-type-observation-list'>
                      <span>All wall/ceiling/floor repairs appear complete (circle one)</span>
                      <div>
                      <Controller
                        name="repairs_completed"
                        control={control}
                        render={({ field }) => {
                          const value = field.value ?? formData.repairs_completed ?? false;
                          return (
                            <>
                              <label style={{ marginLeft: '12px', marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === true}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(true)}
                                />
                                Yes
                              </label>
                              <label style={{ marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === false}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(false)}
                                />
                                No
                              </label>
                            </>
                          );
                        }}
                      />
                      </div>
                    </li>
                    <li className='post-type-observation-list'>
                      <span>Ventilation appears adequate (circle one)</span>
                      <div>
                      <Controller
                        name="ventilation_adequate"
                        control={control}
                        render={({ field }) => {
                          const value = field.value ?? formData.ventilation_adequate ?? false;
                          return (
                            <>
                              <label style={{ marginLeft: '12px', marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === true}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(true)}
                                />
                                Yes
                              </label>
                              <label style={{ marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === false}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(false)}
                                />
                                No
                              </label>
                            </>
                          );
                        }}
                      />
                      </div>
                    </li>
                    <li className='post-type-observation-list'>
                      <span>Moisture source appears corrected (circle one)</span>
                      <div>
                      <Controller
                        name="moisture_source_corrected"
                        control={control}
                        render={({ field }) => {
                          const value = field.value ?? formData.moisture_source_corrected ?? false;
                          return (
                            <>
                              <label style={{ marginLeft: '12px', marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === true}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(true)}
                                />
                                Yes
                              </label>
                              <label style={{ marginRight: '8px' }}>
                                <input
                                  type="radio"
                                  checked={value === false}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(false)}
                                />
                                No
                              </label>
                            </>
                          );
                        }}
                      />
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

           
          </>
        )}

        <section className="mold-section">
          <div className="mold-section-title">ASSESSMENT CONCLUSION</div>
          <div className="mold-section-divider" />
          <div className="mold-conclusion-box">
            {isEditMode ? (
              <textarea
                className="mold-input"
                {...register('final_conclusion')}
                rows={4}
                style={{ width: '100%', resize: 'vertical' }}
              />
            ) : (
              <span style={{ whiteSpace: 'pre-line' }}>{formData.final_conclusion || ''}</span>
            )}
          </div>
        </section>

        {
            moldType === 'post' && ( <section className="mold-section">
                <div className="mold-section-title">CLEARANCE DETERMINATION</div>
                <div className="mold-section-divider" />
                <div className="mold-highlight-box">
                  <div className="mold-background-content">
                    <div className="mold-background-intro" style={{ marginBottom: '16px' }}>
                      Based on the post-remediation visual inspection:
                    </div>
  
                    <Controller
                      name="clearance_passed"
                      control={control}
                      render={({ field }) => {
                        const value = field.value ?? formData.clearance_passed;
                        return (
                          <>
                           {(isEditMode || formData.clearance_passed === true) && <div style={{ marginBottom: '20px' }}>
                              <label className="mold-checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                <input
                                  type="radio"
                                  name="clearance_passed"
                                  checked={value === true}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(true)}
                                  style={{ marginTop: '4px', marginRight: '8px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <strong>CLEARANCE PASSED</strong>
                                  <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                                    At the time of inspection, all areas are <strong>clean, dry, and free of visible mold</strong>, and remediation appears completed in accordance with NYC Local Law 55 and the approved work plan.
                                  </div>
                                  <div>
                                    Recommendation: HPD may remove Violation #
                                      <strong style={{ marginLeft: '8px' }}>{formData.violation_id}</strong>
                                    .
                                  </div>
                                </div>
                              </label>
                            </div>}
  
                            {(isEditMode || formData.clearance_passed === false) && <div>
                              <label className="mold-checkbox-label" style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <input
                                  type="radio"
                                  name="clearance_passed"
                                  checked={value === false}
                                  disabled={!isEditMode}
                                  onChange={() => field.onChange(false)}
                                  style={{ marginTop: '4px', marginRight: '8px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <strong>CLEARANCE FAILED</strong>
                                  <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                                    Additional work is required before clearance can be recommended.
                                  </div>
                                  <div>
                                    Required corrective actions:
                                    <div style={{ marginTop: '8px' }}>
                                        {formData.clearance_corrective_actions }
                                      </div>
                                  </div>
                                </div>
                              </label>
                            </div>
                            }
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
              </section>)
        }

       {moldType === 'pre' && <section className="mold-section">
          <div className="mold-section-title">MOLD REMEDIATION ESTIMATE:</div>
          <div className="mold-section-divider" />
          <div className="mold-estimate-box">
            <div className="mold-estimate-content">
              <div className="mold-estimate-intro">
                Please obtain quotes from a few EPA approved contractors to confirm pricing:
              </div>
              <div className="mold-estimate-amount">
                Abatement{' '}
                {isEditMode ? (
                  <>
                    $<input
                      type="text"
                      className="mold-input-inline"
                      {...register('estimated_cost')}
                    />{' '}
                   
                  </>
                ) : (
                  <strong>${formData.estimated_cost || ''}</strong>
                )}{' '}
                + tax approximate.
              </div>
              <div className="mold-estimate-exclusions">
                <div className="mold-exclusions-title">The following items are not included:</div>
                <ul className="mold-exclusions-list">
                  <li>Repairs, leaks and services subcontractor costs are not included.</li>
                  <li>Tile & grout repair work not included.</li>
                  <li>Mechanical ventilation not included.</li>
                  <li>Hazardous materials removal not included.</li>
                  <li>Assessor fees & filing not included.</li>
                </ul>
              </div>
              <div className="mold-estimate-disclaimer">
                <strong>These are estimates ONLY.</strong>
              </div>
            </div>
          </div>
        </section>}
      </div>
    </form>
  );
};

export default Mold;
